/**
 * MosaicScreenerModal.js — v6.32.163
 *
 * 10-minute Micro-Screener. Launched from the MosaicIntakeModal "Run
 * Screener" button. Walks the teacher through 12 items (one per row),
 * each scored on a quick 0 / 0.5 / 1 scale (No / Partial / Yes), then
 * computes per-domain MOSAIC levels via the backend and (optionally)
 * persists them onto the student's intake row.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import MosaicActionPanel from './MosaicActionPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_OPTIONS = [
  { v: 0,   label: 'No',      color: 'bg-rose-100 border-rose-300 text-rose-800' },
  { v: 0.5, label: 'Partial', color: 'bg-amber-100 border-amber-300 text-amber-800' },
  { v: 1,   label: 'Yes',     color: 'bg-emerald-100 border-emerald-300 text-emerald-800' },
];

const MosaicScreenerModal = ({ open, onOpenChange, student, grade, onComplete }) => {
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setItems([]); setResponses({}); setResult(null);
    setLoading(true);
    axios
      .get(`${API}/mosaic/screener/items`, { params: { grade: grade || '3' } })
      .then((res) => setItems(res.data?.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load screener'))
      .finally(() => setLoading(false));
  }, [open, grade]);

  const setScore = (item_id, v) => setResponses((r) => ({ ...r, [item_id]: v }));

  const handleSubmit = async (persist) => {
    if (!student?.user_id && persist) {
      toast.error('No student selected to persist to');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/mosaic/screener/submit`, {
        grade: grade || '3',
        responses,
        student_id: persist ? student?.user_id : undefined,
        persist,
      });
      setResult(res.data);
      toast.success(persist ? 'Screener saved to student intake' : 'Screener scored');
      if (persist && onComplete) onComplete(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit screener');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mosaic-screener-modal" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Sparkles className="w-5 h-5 text-violet-600" />
            MOSAIC 10-Minute Micro-Screener
          </DialogTitle>
          <DialogDescription>
            {student?.name ? `Student: ${student.name} · Grade ${grade || '?'}` : `Grade ${grade || '?'}`} ·
            <span className="ml-1 text-gray-500">12 quick items across all 4 domains. Mark Yes / Partial / No per item.</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading items…
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it, idx) => (
              <div
                key={it.id}
                data-testid={`screener-item-${it.id}`}
                className="border border-gray-300 p-3"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-gray-400">#{idx + 1}</span>
                    <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
                      {it.domain} · target L{it.target_level}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-800 mb-2">{it.prompt}</p>
                <div className="flex gap-2">
                  {SCORE_OPTIONS.map((opt) => {
                    const on = responses[it.id] === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setScore(it.id, opt.v)}
                        data-testid={`screener-score-${it.id}-${opt.v}`}
                        className={`px-3 py-1 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
                          on ? opt.color : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => handleSubmit(false)}
                disabled={submitting || items.length === 0}
                data-testid="screener-score-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Score (preview)
              </Button>
              <Button
                onClick={() => handleSubmit(true)}
                disabled={submitting || items.length === 0 || !student?.user_id}
                data-testid="screener-save-btn"
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save to intake
              </Button>
            </div>

            {result?.panel ? (
              <div className="pt-3" data-testid="screener-result">
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                  Result
                </h4>
                <MosaicActionPanel panel={result.panel} />
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MosaicScreenerModal;
